"use client";

import { useEffect, useState } from "react";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import styles from "../css/dashboard.module.css";
import { getTotalBranches, getExpiredStockKpi, getLowStockKpi, getSalesByBranch } from "../../../api/analytics/analyticsApi.js";
import { useAuth } from "../../../context/AuthContext";
import { getBranches as getBranchesForUserManagement } from "../../../api/userManagement/userManagementApi";
import modalStyles from "../../../css/modal.module.css";

import TemperatureMonitoringCard from "../components/TemperatureMonitoringCard";
import TotalBranchesCard from "../components/TotalBranchesCard";
import LowStockCard from "../components/LowStockCard";
import ExpiringStockCard from "../components/ExpiringStockCard";
import DashboardModal from "../components/DashboardModal";
import SalesByBranchSection from "../components/SalesByBranchSection";

export default function PharmacyDashboard() {
  const { user } = useAuth();
  const roleId = Number(user?.roleid);
  const userBranchCode = user?.branchcode ? String(user.branchcode) : "";

  const [tempByBranch, setTempByBranch] = useState({});

  const [totalBranches, setTotalBranches] = useState(null);
  const [branchesError, setBranchesError] = useState(null);
  const [branchList, setBranchList] = useState([]);
  const [branchListError, setBranchListError] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [tempPage, setTempPage] = useState(1);
  const [branchesPage, setBranchesPage] = useState(1);

  const [expiredKpi, setExpiredKpi] = useState({
    totalExpiredStock: null,
    expiredAcrossBranches: null,
    nearingExpiryStock: null,
  });
  const [expiredKpiError, setExpiredKpiError] = useState(null);
  const [lowKpi, setLowKpi] = useState({
    totalLowStock: null,
    lowAcrossBranches: null,
    criticalStock: null,
    lowBranches: [],
  });
  const [lowKpiError, setLowKpiError] = useState(null);
  const [salesByBranchData, setSalesByBranchData] = useState([]);
  const [salesByBranchError, setSalesByBranchError] = useState(null);

  const safeBranchName = (b) =>
    b?.branchname || b?.branchName || b?.name || b?.branchcode || b?.branchCode || "Unknown Branch";

  const safeBranchCode = (b) =>
    b?.branchcode || b?.branchCode || b?.code || "";

  const normalizeBranches = (data) => {
    const list = Array.isArray(data) ? data : [];
    return list
      .map((b) => ({
        branchcode: safeBranchCode(b) ? String(safeBranchCode(b)) : "",
        branchname: String(safeBranchName(b)),
        isactive: b?.isactive ?? b?.isActive,
      }))
      .filter((b) => b.branchcode || b.branchname)
      .sort((a, b) => a.branchname.localeCompare(b.branchname));
  };

  const getTempStatus = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return "unknown";
    return n <= 8 ? "cold" : n <= 25 ? "ok" : "warn";
  };

  const getTempClass = (status) => {
    if (status === "warn") return styles.tempHot;
    if (status === "ok") return styles.tempOk;
    if (status === "cold") return styles.tempCold;
    return styles.temp;
  };

  const TEMP_PAGE_SIZE = 6;
  const BRANCHES_PAGE_SIZE = 5;

  const openModal = (key) => setActiveModal(key);
  const closeModal = () => setActiveModal(null);

  const apiBase =
    process.env.REACT_APP_API_URL ||
    (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");

  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    const url = `${apiBase}/api/temp-sensors/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    const onReading = (event) => {
      try {
        const data = JSON.parse(event.data);
        const bc = data?.branchcode ? String(data.branchcode) : "";
        if (!bc) return;
        setTempByBranch((prev) => ({
          ...prev,
          [bc]: {
            temp: data?.temp1 ?? null,
            timestamp: data?.timestamp ?? null,
            branchname: data?.branchname ?? null,
          },
        }));
      } catch (_) {}
    };

    es.addEventListener("reading", onReading);
    es.onerror = () => {
      try {
        es.close();
      } catch (_) {}
    };

    return () => {
      try {
        es.removeEventListener("reading", onReading);
      } catch (_) {}
      try {
        es.close();
      } catch (_) {}
    };
  }, [user, apiBase]);

  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    let cancelled = false;

    const applyReading = (reading) => {
      const bc = reading?.branchcode ? String(reading.branchcode) : "";
      if (!bc) return;
      const tempVal = reading?.temp1 ?? null;
      setTempByBranch((prev) => ({
        ...prev,
        [bc]: {
          temp: tempVal,
          timestamp: reading?.timestamp ?? null,
          branchname: reading?.branchname ?? null,
        },
      }));
    };

    const fetchLatest = async (branchcode) => {
      const qs = new URLSearchParams();
      qs.set("token", token);
      if (branchcode) qs.set("branchcode", branchcode);
      const res = await fetch(`${apiBase}/api/temp-sensors/latest?${qs.toString()}`);
      if (!res.ok) return null;
      const data = await res.json();
      return data?.reading || null;
    };

    (async () => {
      try {
        if (roleId === 2) {
          const codes = (branchList || [])
            .map((b) => (b?.branchcode ? String(b.branchcode) : ""))
            .filter(Boolean);

          const results = await Promise.allSettled(codes.map((c) => fetchLatest(c)));
          if (cancelled) return;
          for (const r of results) {
            if (r.status !== "fulfilled") continue;
            if (!r.value) continue;
            applyReading(r.value);
          }
          return;
        }

        if (!userBranchCode) return;
        const reading = await fetchLatest();
        if (cancelled) return;
        if (reading) applyReading(reading);
      } catch (_) {}
    })();

    return () => {
      cancelled = true;
    };
  }, [user, roleId, userBranchCode, branchList, apiBase]);

  useEffect(() => {
    let isMounted = true;

    async function loadBranches() {
      try {
        setBranchesError(null);
        const val = await getTotalBranches();
        if (!isMounted) return;
        setTotalBranches(val);
      } catch (err) {
        if (!isMounted) return;
        setBranchesError(err?.message || "Failed to load total branches");
      }
    }

    async function loadExpiredKpi() {
      try {
        setExpiredKpiError(null);
        const data = await getExpiredStockKpi();
        if (!isMounted) return;
        setExpiredKpi({
          totalExpiredStock: data.totalExpiredStock ?? 0,
          expiredAcrossBranches: data.expiredAcrossBranches ?? 0,
          nearingExpiryStock: data.nearingExpiryStock ?? 0,
        });
      } catch (err) {
        if (!isMounted) return;
        setExpiredKpiError(err?.message || "Failed to load expiring stock KPI");
      }
    }

    async function loadLowKpi() {
      try {
        setLowKpiError(null);
        const data = await getLowStockKpi();
        if (!isMounted) return;
        setLowKpi({
          totalLowStock: data.totalLowStock ?? 0,
          lowAcrossBranches: data.lowAcrossBranches ?? 0,
          criticalStock: data.criticalStock ?? 0,
          lowBranches: Array.isArray(data.lowBranches) ? data.lowBranches : [],
        });
      } catch (err) {
        if (!isMounted) return;
        setLowKpiError(err?.message || "Failed to load low stock KPI");
      }
    }

    async function loadSalesByBranch() {
      try {
        setSalesByBranchError(null);
        const data = await getSalesByBranch();
        if (!isMounted) return;

        const chartData = (data || []).map((entry) => ({
          name: entry.branchName ?? "Unknown Branch",
          // use full Rand amount for revenue
          value: Number(entry.revenue ?? 0),
        }));

        setSalesByBranchData(chartData);
      } catch (err) {
        if (!isMounted) return;
        setSalesByBranchError(err?.message || "Failed to load sales by branch");
      }
    }

    loadBranches();
    loadExpiredKpi();
    loadLowKpi();
    loadSalesByBranch();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadBranchList() {
      try {
        setBranchListError(null);
        const data = await getBranchesForUserManagement();
        if (!isMounted) return;
        const normalized = normalizeBranches(data);
        setBranchList(normalized);
      } catch (err) {
        if (!isMounted) return;
        setBranchListError(err?.message || "Failed to load branches");
      }
    }

    loadBranchList();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!activeModal) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeModal]);

  useEffect(() => {
    if (activeModal !== "temp") return;
    setTempPage(1);
  }, [activeModal, roleId, userBranchCode, branchList.length]);

  useEffect(() => {
    if (activeModal !== "branches") return;
    setBranchesPage(1);
  }, [activeModal, branchList.length]);

  const branchesDisplay =
    typeof totalBranches === "number" ? totalBranches.toString() : "--";

  const totalExpiredDisplay =
    expiredKpi.totalExpiredStock != null
      ? expiredKpi.totalExpiredStock.toString()
      : "--";

  const expiredBranchesDisplay =
    expiredKpi.expiredAcrossBranches != null
      ? expiredKpi.expiredAcrossBranches.toString()
      : "0";

  const nearingExpiryDisplay =
    expiredKpi.nearingExpiryStock != null
      ? expiredKpi.nearingExpiryStock.toString()
      : "0";

  const totalLowDisplay =
    lowKpi.totalLowStock != null ? lowKpi.totalLowStock.toString() : "--";

  const lowBranchesDisplay =
    lowKpi.lowAcrossBranches != null ? lowKpi.lowAcrossBranches.toString() : "0";

  const criticalLowDisplay =
    lowKpi.criticalStock != null ? lowKpi.criticalStock.toString() : "0";

  const lowBranchNames = Array.isArray(lowKpi.lowBranches)
    ? lowKpi.lowBranches
        .map((b) => b?.branchName)
        .filter(Boolean)
    : [];

  const visibleTempBranches = (() => {
    if (roleId === 2) return branchList;
    const match = branchList.find((b) => String(b.branchcode) === String(userBranchCode));
    return match ? [match] : userBranchCode ? [{ branchcode: userBranchCode, branchname: userBranchCode }] : [];
  })();

  const fullTempRows = (visibleTempBranches || []).map((b) => {
    const bc = String(b.branchcode || "");
    const cached = bc ? tempByBranch[bc] : null;
    const tempVal = cached?.temp;
    const status = getTempStatus(tempVal);
    return {
      branchcode: b.branchcode,
      branchname: b.branchname,
      temp: Number.isFinite(Number(tempVal)) ? Math.round(Number(tempVal) * 10) / 10 : null,
      status,
    };
  });

  const tempPageCount = Math.max(1, Math.ceil(fullTempRows.length / TEMP_PAGE_SIZE));
  const safeTempPage = Math.min(Math.max(1, tempPage), tempPageCount);
  const tempPageStart = (safeTempPage - 1) * TEMP_PAGE_SIZE;
  const tempRowsPage = fullTempRows.slice(tempPageStart, tempPageStart + TEMP_PAGE_SIZE);

  const branchesPageCount = Math.max(1, Math.ceil(branchList.length / BRANCHES_PAGE_SIZE));
  const safeBranchesPage = Math.min(Math.max(1, branchesPage), branchesPageCount);
  const branchesPageStart = (safeBranchesPage - 1) * BRANCHES_PAGE_SIZE;
  const branchesRowsPage = branchList.slice(
    branchesPageStart,
    branchesPageStart + BRANCHES_PAGE_SIZE
  );

  return (
    <div className={styles.app}>
      {/* Main Content */}
      <div className={styles.mainContainer}>
        <Navbar backTo="/dashboard" />

        <main className={styles.content}>
          <div className={styles.metricsGrid}>
            <TemperatureMonitoringCard
              styles={styles}
              onOpen={() => openModal("temp")}
              branchListError={branchListError}
              roleId={roleId}
              userBranchCode={userBranchCode}
              fullTempRows={fullTempRows}
              getTempClass={getTempClass}
            />

            <TotalBranchesCard
              styles={styles}
              branchesDisplay={branchesDisplay}
              branchesError={branchesError}
              branchListError={branchListError}
              onOpen={() => openModal("branches")}
            />

            <LowStockCard
              styles={styles}
              totalLowDisplay={totalLowDisplay}
              lowBranchesDisplay={lowBranchesDisplay}
              criticalLowDisplay={criticalLowDisplay}
              lowKpiError={lowKpiError}
              onOpen={() => openModal("low")}
            />

            <ExpiringStockCard
              styles={styles}
              totalExpiredDisplay={totalExpiredDisplay}
              expiredBranchesDisplay={expiredBranchesDisplay}
              nearingExpiryDisplay={nearingExpiryDisplay}
              expiredKpiError={expiredKpiError}
              onOpen={() => openModal("expiry")}
            />
          </div>

          <DashboardModal
            styles={styles}
            modalStyles={modalStyles}
            activeModal={activeModal}
            closeModal={closeModal}
            roleId={roleId}
            userBranchCode={userBranchCode}
            branchListError={branchListError}
            fullTempRows={fullTempRows}
            tempRowsPage={tempRowsPage}
            getTempClass={getTempClass}
            safeTempPage={safeTempPage}
            tempPageCount={tempPageCount}
            onTempPageChange={(p) => setTempPage(p)}
            TEMP_PAGE_SIZE={TEMP_PAGE_SIZE}
            branchesError={branchesError}
            branchListError2={branchListError}
            branchList={branchList}
            branchesRowsPage={branchesRowsPage}
            safeBranchesPage={safeBranchesPage}
            branchesPageCount={branchesPageCount}
            onBranchesPageChange={(p) => setBranchesPage(p)}
            BRANCHES_PAGE_SIZE={BRANCHES_PAGE_SIZE}
            totalLowDisplay={totalLowDisplay}
            criticalLowDisplay={criticalLowDisplay}
            lowBranchNames={lowBranchNames}
            lowKpiError={lowKpiError}
            totalExpiredDisplay={totalExpiredDisplay}
            nearingExpiryDisplay={nearingExpiryDisplay}
            expiredBranchesDisplay={expiredBranchesDisplay}
            expiredKpiError={expiredKpiError}
          />

          <SalesByBranchSection
            styles={styles}
            salesByBranchData={salesByBranchData}
            salesByBranchError={salesByBranchError}
          />
        </main>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}